import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Badge
} from '@mui/material';
import {
  Visibility,
  Download,
  Search,
  FilterList,
  Security,
  Warning,
  PersonAdd,
  Edit,
  Delete,
  AttachMoney,
  Settings,
  CloudDownload,
  VpnKey,
  Shield,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`audit-tabpanel-${index}`}
      aria-labelledby={`audit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface AuditLog {
  id: string;
  adminUsername: string;
  adminName: string;
  actionType: string;
  actionCategory: string;
  description: string;
  timestamp: string;
  ipAddress: string;
  affectedResourceId?: string;
  affectedResourceType?: string;
  beforeValue?: string;
  afterValue?: string;
  severity: 'info' | 'warning' | 'critical';
}

interface LoginHistory {
  id: string;
  adminUsername: string;
  adminName: string;
  status: 'success' | 'failed';
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  failureReason?: string;
}

interface AdminUser {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  role: string;
  status: 'active' | 'inactive';
  lastLogin?: string;
  createdAt: string;
}

const AuditLogsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7');

  // Stats
  const [stats, setStats] = useState({
    totalLogs: 0,
    todayLogs: 0,
    criticalEvents: 0,
    failedLogins: 0,
    activeAdmins: 0
  });

  // Log Details Dialog
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Admin Management Dialog
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminFormData, setAdminFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    preferredName: '',
    role: 'SUPPORT_AGENT',
    password: ''
  });

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchData();
  }, [tabValue, categoryFilter, severityFilter, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      if (tabValue === 0) {
        // Audit Logs
        const response = await axios.get(
          `${apiUrl}/admin/audit-logs?category=${categoryFilter}&severity=${severityFilter}&days=${dateRange}`,
          { headers }
        );
        setAuditLogs(response.data.logs || []);
        setStats(response.data.stats || stats);
      } else if (tabValue === 1) {
        // Login History
        const response = await axios.get(`${apiUrl}/admin/audit-logs/logins?days=${dateRange}`, { headers });
        setLoginHistory(response.data.logins || []);
      } else if (tabValue === 2) {
        // Admin Management
        const response = await axios.get(`${apiUrl}/admin/admins`, { headers });
        setAdminUsers(response.data.admins || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportLogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${apiUrl}/admin/audit-logs/export?days=${dateRange}&format=csv`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showAlert('success', 'Audit logs exported successfully');
    } catch (error: any) {
      showAlert('error', 'Failed to export logs');
    }
  };

  const handleCreateAdmin = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/admins`,
        adminFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Admin user created successfully');
      setAdminDialogOpen(false);
      setAdminFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        preferredName: '',
        role: 'SUPPORT_AGENT',
        password: ''
      });
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to create admin user');
    }
  };

  const handleToggleAdminStatus = async (adminId: string, currentStatus: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';

      await axios.patch(
        `${apiUrl}/admin/admins/${adminId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', `Admin ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'} successfully`);
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update admin status');
    }
  };

  const handleResetPassword = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to force a password reset for this admin?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/admins/${adminId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Password reset email sent to admin');
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send password reset');
    }
  };

  const showAlert = (severity: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'USER_MODIFIED': return <Edit color="primary" />;
      case 'USER_CREATED': return <PersonAdd color="success" />;
      case 'USER_DELETED': return <Delete color="error" />;
      case 'FINANCIAL_TRANSACTION': return <AttachMoney color="warning" />;
      case 'CONFIG_CHANGED': return <Settings color="info" />;
      case 'DATA_EXPORTED': return <CloudDownload color="action" />;
      case 'LOGIN': return <VpnKey color="success" />;
      case 'LOGOUT': return <VpnKey color="action" />;
      default: return <Security color="action" />;
    }
  };

  const getSeverityColor = (severity: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'success';
      default: return 'default';
    }
  };

  const actionCategories = [
    'User Management',
    'Ride Management',
    'Financial',
    'Configuration',
    'Security',
    'Data Export',
    'Verification'
  ];

  const adminRoles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin' },
    { value: 'SUPPORT_AGENT', label: 'Support Agent' },
    { value: 'FINANCE_MANAGER', label: 'Finance Manager' },
    { value: 'OPERATIONS_MANAGER', label: 'Operations Manager' }
  ];

  const filteredLogs = auditLogs.filter(log => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.adminUsername.toLowerCase().includes(query) ||
        log.description.toLowerCase().includes(query) ||
        log.actionType.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Security & Audit Logs
        </Typography>
        {tabValue === 0 && (
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportLogs}
          >
            Export Logs
          </Button>
        )}
        {tabValue === 2 && (
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setAdminDialogOpen(true)}
          >
            Add Admin
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Total Logs
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats.totalLogs}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Today's Logs
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {stats.todayLogs}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Critical Events
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="error.main">
              {stats.criticalEvents}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Failed Logins
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {stats.failedLogins}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Active Admins
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats.activeAdmins}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<Security />} label="Audit Logs" iconPosition="start" />
          <Tab icon={<VpnKey />} label="Login History" iconPosition="start" />
          <Tab icon={<Shield />} label="Admin Management" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Audit Logs Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <TextField
              placeholder="Search logs..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <FormControl size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {actionCategories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={severityFilter}
                label="Severity"
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="1">Last 24 Hours</MenuItem>
                <MenuItem value="7">Last 7 Days</MenuItem>
                <MenuItem value="30">Last 30 Days</MenuItem>
                <MenuItem value="90">Last 90 Days</MenuItem>
                <MenuItem value="365">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Logs Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(log.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>{log.adminName[0]}</Avatar>
                      <Box>
                        <Typography variant="body2">{log.adminName}</Typography>
                        <Typography variant="caption" color="textSecondary">@{log.adminUsername}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getActionIcon(log.actionType)}
                      <Typography variant="caption">{log.actionType.replace(/_/g, ' ')}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.actionCategory} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.severity} size="small" color={getSeverityColor(log.severity)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{log.ipAddress}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => {
                      setSelectedLog(log);
                      setDetailsOpen(true);
                    }}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      {/* Login History Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>User Agent</TableCell>
                <TableCell>Failure Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginHistory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((login) => (
                <TableRow key={login.id} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(login.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>{login.adminName[0]}</Avatar>
                      <Box>
                        <Typography variant="body2">{login.adminName}</Typography>
                        <Typography variant="caption" color="textSecondary">@{login.adminUsername}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={login.status}
                      size="small"
                      color={login.status === 'success' ? 'success' : 'error'}
                      icon={login.status === 'failed' ? <ErrorIcon /> : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{login.ipAddress}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {login.userAgent}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {login.failureReason && (
                      <Typography variant="caption" color="error.main">{login.failureReason}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={loginHistory.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      {/* Admin Management Tab */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Admin</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminUsers.map((admin) => (
                <TableRow key={admin.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar>{(admin.preferredName || admin.firstName || '?')[0]}{(admin.lastName || '?')[0]}</Avatar>
                      <Typography variant="body2">{admin.preferredName || admin.firstName} {admin.lastName ? admin.lastName.charAt(0) + '.' : ''}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">@{admin.username}</Typography>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Chip label={admin.role.replace(/_/g, ' ')} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={admin.status}
                      size="small"
                      color={admin.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleToggleAdminStatus(admin.id, admin.status)}
                    >
                      {admin.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleResetPassword(admin.id)}
                    >
                      Reset Password
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Log Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">Admin</Typography>
                <Typography variant="body1">{selectedLog.adminName} (@{selectedLog.adminUsername})</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Action Type</Typography>
                <Typography variant="body1">{selectedLog.actionType.replace(/_/g, ' ')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Category</Typography>
                <Typography variant="body1">{selectedLog.actionCategory}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Description</Typography>
                <Typography variant="body1">{selectedLog.description}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Timestamp</Typography>
                <Typography variant="body1">{new Date(selectedLog.timestamp).toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">IP Address</Typography>
                <Typography variant="body1" fontFamily="monospace">{selectedLog.ipAddress}</Typography>
              </Box>
              {selectedLog.affectedResourceType && (
                <Box>
                  <Typography variant="caption" color="textSecondary">Affected Resource</Typography>
                  <Typography variant="body1">
                    {selectedLog.affectedResourceType} (ID: {selectedLog.affectedResourceId})
                  </Typography>
                </Box>
              )}
              {selectedLog.beforeValue && selectedLog.afterValue && (
                <Box>
                  <Typography variant="caption" color="textSecondary">Changes</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                    <Typography variant="body2" color="error.main">
                      Before: {selectedLog.beforeValue}
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      After: {selectedLog.afterValue}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="textSecondary">Severity</Typography>
                <Chip label={selectedLog.severity} color={getSeverityColor(selectedLog.severity)} size="small" />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={adminDialogOpen} onClose={() => setAdminDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={adminFormData.username}
              onChange={(e) => setAdminFormData({ ...adminFormData, username: e.target.value })}
              required
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={adminFormData.email}
              onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
              required
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={adminFormData.preferredName || adminFormData.firstName}
                onChange={(e) => setAdminFormData({ ...adminFormData, firstName: e.target.value })}
                required
              />
              <TextField
                label="Last Name"
                fullWidth
                value={adminFormData.lastName}
                onChange={(e) => setAdminFormData({ ...adminFormData, lastName: e.target.value })}
                required
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={adminFormData.role}
                label="Role"
                onChange={(e) => setAdminFormData({ ...adminFormData, role: e.target.value })}
              >
                {adminRoles.map(role => (
                  <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={adminFormData.password}
              onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
              helperText="Minimum 8 characters"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAdmin}>
            Create Admin
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditLogsPage;
